/**
 * Form Rules > "Select Forms or Bundles" list and the Save Rule check (credifyfbs#301, #302,
 * #303). Pure, so the tests can pin them.
 */
import { isSystemForm } from './systemForms.js';

export interface RuleFormRow {
  id: string;
  name: string;
  description?: string | null;
  /** "builder" = imported from the Form Builder (POST /forms/import-builder). */
  source?: string | null;
}

export interface RuleBundleRow {
  id: string;
  name: string;
  description?: string | null;
  createdAt?: string | null;
  _count?: { packetForms?: number } | null;
}

/**
 * credifyfbs#301: the system forms Form Rules offers, one per name. A practice that edited a
 * built-in template in the Form Builder (its own "Informed Consent for Treatment") has that copy
 * imported as a builder form with the same name and, usually, no description, beside the seeded
 * form. Both passed the name check, so the form showed twice. Keep the seeded form: it is the one
 * bundles are built from, and the import already gives it the practice's edited layout
 * (portal#34). A builder copy shows only when there is no seeded form of that name.
 */
export function ruleCatalogForms<T extends RuleFormRow>(forms: readonly T[]): T[] {
  const byName = new Map<string, T>();
  for (const f of forms) {
    if (!isSystemForm(f.name)) continue;
    const had = byName.get(f.name);
    if (!had) {
      byName.set(f.name, f);
      continue;
    }
    const hadSeeded = had.source !== 'builder';
    const isSeeded = f.source !== 'builder';
    const keep = !hadSeeded && isSeeded ? f : had;
    const other = keep === f ? had : f;
    // Never lose a description: the copy left out may be the only one that has it.
    byName.set(
      f.name,
      keep.description || !other.description ? keep : { ...keep, description: other.description },
    );
  }
  // Keep the API's order (A to Z by name).
  return [...byName.values()];
}

const fmtDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * credifyfbs#302: two bundles can share a name (nothing stops a second "test"). They are two
 * real bundles, so both stay, each with a short hint that tells them apart: how many forms it
 * has and the day it was made (and the time, when that is still the same). Bundles with a name
 * of their own get no hint.
 */
export function bundleHints(bundles: readonly RuleBundleRow[]): Map<string, string> {
  const groups = new Map<string, RuleBundleRow[]>();
  for (const b of bundles) {
    const key = b.name.trim().toLowerCase();
    groups.set(key, [...(groups.get(key) ?? []), b]);
  }
  const hints = new Map<string, string>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const base = (b: RuleBundleRow, withTime: boolean) => {
      const n = b._count?.packetForms;
      const parts: string[] = [];
      if (typeof n === 'number') parts.push(`${n} form${n === 1 ? '' : 's'}`);
      if (b.createdAt)
        parts.push(
          `created ${fmtDay(b.createdAt)}${withTime ? ` at ${fmtTime(b.createdAt)}` : ''}`,
        );
      return parts.join(', ');
    };
    const short = group.map((b) => base(b, false));
    const clash = new Set(short.filter((s, i) => short.indexOf(s) !== i));
    group.forEach((b, i) => {
      const hint = clash.has(short[i]!) ? base(b, true) : short[i]!;
      if (hint) hints.set(b.id, hint);
    });
  }
  return hints;
}

export interface RuleDraft {
  /** forms and bundles checked */
  selected: number;
  recClient: boolean;
  recClinician: boolean;
  recRole: boolean;
  recUser: boolean;
  /** the user picked for "Specific user" ('' = none yet) */
  userValue: string;
  /** each trigger condition's type ('' = still "Select one...") */
  triggerTypes: readonly string[];
}

/**
 * credifyfbs#303: what is still missing before a rule can be saved: a form or bundle, someone to
 * send it to, and a trigger (every condition needs its type). Empty = ready to save.
 */
export function ruleSaveMissing(d: RuleDraft): string[] {
  const missing: string[] = [];
  if (d.selected === 0) missing.push('a form or bundle');
  const hasRecipient = d.recClient || d.recClinician || d.recRole || (d.recUser && !!d.userValue);
  if (!hasRecipient) missing.push('a recipient');
  if (d.triggerTypes.length === 0 || d.triggerTypes.some((t) => !t)) missing.push('a trigger');
  return missing;
}

/** The hint beside a disabled Save Rule: "Choose a form or bundle and a trigger to save." */
export function ruleSaveHint(missing: readonly string[]): string {
  if (!missing.length) return '';
  const list =
    missing.length === 1
      ? missing[0]!
      : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]!}`;
  return `Choose ${list} to save this rule.`;
}
